/* eslint-disable react/display-name */
import { createStore } from 'vuex'
import channel from '../../../socket.ts'
import { MEMBER_CHAT_SCOPE } from './route_helpers'

const NEW_MESSAGE_NOTIFICATION = 'NEW_MESSAGE_NOTIFICATION'
const MESSAGE_SENT_NOTIFICATION = 'MESSAGE_SENT_NOTIFICATION'
const REMOVE_NOTIFICATION_AFTER = 5000

const initialState = {
  chats: null,
  notifications: [],
  defaultChatScope: null
}

const channelPush = (msg, payload) => {
  // undefined payloads are not allowed will cause errors in phx 1.5.7+
  // see https://hexdocs.pm/phoenix/js/#channelpush
  if (payload === undefined) {
    payload = {}
  }
  return new Promise((resolve, reject) => {
    channel.push(msg, payload).receive('ok', resolve).receive('error', reject)
  })
}

const findChat = (chatScope, partnerId) => {
  return chatScope.chats.find(chat => {
    if (chatScope.path === MEMBER_CHAT_SCOPE) {
      return chat.partner.publication.id === partnerId
    } else {
      return chat.partner.member.id === partnerId
    }
  })
}

const mutations = {
  setChats(state, scopes) {
    state.chats = scopes
  },

  setChatMessages(state, { messages, scope, partnerId, fetchedPagesCount }) {
    state.chats = state.chats.map(chatScope => {
      if (chatScope.path === scope) {
        const selectedChat = findChat(chatScope, partnerId)

        // Merge old messages array with new messages array, removing possible duplicates
        if (messages.length > 0) {
          selectedChat.messages.forEach(oldMsg => {
            const idx = messages.findIndex(newMsg => newMsg.id === oldMsg.id)
            if (idx === -1) {
              messages.push(oldMsg)
            }
          })
          selectedChat.messages = messages
          selectedChat.fullyLoaded = false
          selectedChat.fetchedPagesCount = fetchedPagesCount
        } else {
          selectedChat.fullyLoaded = true
        }
      }

      return chatScope
    })
  },

  setNewMessage(state, { message, scope, partnerId }) {
    state.chats = state.chats.map(chatScope => {
      if (chatScope.path === scope) {
        const selectedChat = findChat(chatScope, partnerId)

        selectedChat.messages = [...selectedChat.messages, message]
      }

      return chatScope
    })
  },

  reorderChatsByLastInsertedAt(state, scope) {
    state.chats = state.chats.map(s => {
      if (s.path === scope) {
        s.chats = s.chats.sort((ch1, ch2) => {
          if (ch1.messages.length === 0 && ch2.messages.length === 0) return 0
          if (ch1.messages.length === 0) {
            return 1
          } else if (ch2.messages.length === 0) {
            return -1
          } else {
            // ISO8601 formatted dates are comparable lexicographically
            return ch2.messages[ch2.messages.length - 1].inserted_at.localeCompare(
              ch1.messages[ch1.messages.length - 1].inserted_at
            )
          }
        })
      }

      return s
    })
  },

  updateMessages(state, { messages, scope, partnerId }) {
    state.chats = state.chats.map(chatScope => {
      if (chatScope.path === scope) {
        const selectedChat = findChat(chatScope, partnerId)

        selectedChat.messages = selectedChat.messages.map(msg => {
          return messages.find(newMsg => newMsg.id === msg.id) || msg
        })
      }

      return chatScope
    })
  },

  updateUnreadCountPerChat(state, { scope, partnerId, increment }) {
    state.chats = state.chats.map(chatScope => {
      if (chatScope.path === scope) {
        const selectedChat = findChat(chatScope, partnerId)

        if (selectedChat.count_unread + increment >= 0) {
          selectedChat.count_unread = selectedChat.count_unread + increment
        } else {
          selectedChat.count_unread = 0
        }
      }

      return chatScope
    })
  },

  updateUnreadCountPerScope(state, { scope, increment }) {
    state.chats = state.chats.map(chatScope => {
      if (chatScope.path === scope) {
        if (chatScope.count_unread + increment >= 0) {
          chatScope.count_unread = chatScope.count_unread + increment
        } else {
          chatScope.count_unread = 0
        }
      }

      return chatScope
    })
  },

  setChatsForPublicationScope(state, { route, chats, chatScope: chatScopePath, searchQuery, pagesCount }) {
    const currentChatPath = route.params.chat
    // overwrite all chats, but keep the one that is currently open
    // even if it's not in the new results, so that it doesn't suddenly disappear
    state.chats = state.chats.map(chatScope => {
      if (chatScope.path !== chatScopePath) {
        return chatScope
      }

      let newChats

      if (currentChatPath) {
        const currentChat = findChat(chatScope, currentChatPath)
        let currentChatUpdatedInNewResults = false
        newChats = chats.map(chat => {
          if (chat.partner.member.id === currentChat.partner.member.id) {
            currentChatUpdatedInNewResults = true
            return { ...currentChat, hidden: false }
          } else {
            return chat
          }
        })

        if (!currentChatUpdatedInNewResults) {
          newChats = [...newChats, { ...currentChat, hidden: true }]
        }
      } else {
        newChats = chats
      }
      return {
        ...chatScope,
        searchQuery,
        pagesCount,
        chats: newChats
      }
    })
  },

  appendChatToScope(state, { chat, scope }) {
    const chatScope = state.chats.find(chat => chat.path === scope)
    chatScope.chats = [chat, ...chatScope.chats]
    chatScope.total_chat_count++
  },

  pushNotification(state, { id, data, type }) {
    state.notifications.push({ id, data, type })
  },

  removeNotification(state, { id }) {
    state.notifications = state.notifications.filter(n => n.id !== id)
  }
}

const actions = {
  async fetchChats({ commit }) {
    const data = await channelPush('get_chats_preview')
    const chatsPreview = data.chats_preview
    await commit('setChats', chatsPreview)
    return chatsPreview
  },

  async fetchChatMessages({ commit, state, getters }, { scope, partnerId, fetchedPagesCount }) {
    if (state.chats) {
      const currentChatScope = state.chats.find(chat => chat.path === scope)
      const currentMessages = getters.getChat(scope, partnerId).messages
      const lastInsertedAt = currentMessages.length > 0 && currentMessages[0].inserted_at

      let publicationId
      let withUserId

      if (scope === MEMBER_CHAT_SCOPE) {
        publicationId = partnerId
        withUserId = null
      } else {
        publicationId = currentChatScope.publication.id
        withUserId = partnerId
      }

      const response = await channelPush('get_chat_messages', {
        publication_id: publicationId,
        with_user_id: withUserId,
        last_inserted_at: lastInsertedAt
      })
      await commit('setChatMessages', { messages: response.data, scope, partnerId, fetchedPagesCount })

      return response.data
    }
  },

  async sendMessage({ getters }, { scope, partnerId, body }) {
    const publicationId = getters.getPublicationId(scope, partnerId)

    await channelPush('new_message', {
      publication_id: publicationId,
      recipient_id: partnerId,
      body
    })
  },

  fetchChatsPreviewPublicationScope({ commit, state, getters }, { route, chatScope, searchQuery, pagesCount }) {
    const currentChatScope = getters.getChatScopeByPath(chatScope)
    const payload = { publication_id: currentChatScope.publication.id, search_query: searchQuery, pages: pagesCount }

    channelPush('get_chats_preview_publication_scope', payload).then(response => {
      commit('setChatsForPublicationScope', { route, chats: response.data, chatScope, searchQuery, pagesCount })
    })
  },

  async markMessagesAsRead({ commit }, { scope, partnerId, messageIds }) {
    const { messages } = await channelPush('mark_as_read', { message_ids: messageIds })

    commit('updateMessages', { scope, partnerId, messages })
    commit('updateUnreadCountPerChat', { scope, partnerId, increment: -messages.length })
    commit('updateUnreadCountPerScope', { scope, increment: -messages.length })
  },

  async fetchChat({ commit }, { scopePath, chatPath }) {
    let context, pubSubdomain, memberId

    if (scopePath === MEMBER_CHAT_SCOPE) {
      context = 'user'
      pubSubdomain = chatPath
      memberId = null
    } else {
      context = 'publication'
      pubSubdomain = scopePath
      memberId = chatPath
    }

    const response = await channelPush('get_chat', {
      context,
      publication_subdomain: pubSubdomain,
      member_id: memberId
    })
    const chat = response.chat[0]

    if (chat) {
      commit('appendChatToScope', { chat, scope: scopePath })
      commit('updateUnreadCountPerScope', { scope: scopePath, increment: chat.count_unread })
    }

    return chat
  },

  async setNewMessage({ commit, dispatch, state, getters }, { scope, partnerId, message, context }) {
    const chatScope = state.chats.find(chatScope => chatScope.path === scope)
    const chatForMessage = findChat(chatScope, partnerId)
    const maybeUpdateUnreadCountAndMarkRead = (partnerId, message, updateUnreadCountPerChat = true) => {
      if (message.type === 'received') {
        const openChatPartnerId = getters.getChatPartnerId(state.route.params.chatScope, state.route.params.chat)
        const chatOpen = state.route.params.chatScope === scope && openChatPartnerId === partnerId
        if (document.visibilityState === 'visible' && chatOpen) {
          dispatch('markMessagesAsRead', {
            messageIds: [message.id],
            scope,
            partnerId
          })
        } else {
          if (updateUnreadCountPerChat) {
            commit('updateUnreadCountPerChat', {
              scope,
              partnerId,
              increment: 1
            })
          }
          commit('updateUnreadCountPerScope', {
            scope,
            increment: 1
          })
        }
      }
    }

    if (!chatForMessage) {
      // we received a message for a chat that's not yet loaded, so we load it now
      const response = await channelPush('get_chat_for_message', { message, context })
      const chat = response.data[0]

      commit('appendChatToScope', { chat, scope })
      maybeUpdateUnreadCountAndMarkRead(partnerId, message, false)
    } else {
      commit('setNewMessage', { message, scope, partnerId })
      maybeUpdateUnreadCountAndMarkRead(partnerId, message)
    }
  },

  showNotification({ commit }, { id, data, type }) {
    commit('pushNotification', { id, data, type })
    setTimeout(() => {
      commit('removeNotification', { id })
    }, REMOVE_NOTIFICATION_AFTER)
  }
}

const getters = {
  getDefaultChatScope: state => {
    return state.defaultChatScope
  },

  isInitialDataLoaded: state => {
    return Array.isArray(state.chats)
  },

  getChatScopeByPath: state => path => {
    return state.chats && state.chats.find(scope => scope.path === path)
  },

  getChatScopes: state => {
    return state.chats
  },

  getChatsList: state => currentScope => {
    const chatsList = state.chats && state.chats.find(scope => scope.path === currentScope)

    return chatsList && chatsList.chats.filter(chat => !chat.hidden)
  },

  getTotalChatCount: state => currentScope => {
    const selectedChat = state.chats && state.chats.find(scope => scope.path === currentScope)

    return selectedChat && selectedChat.total_chat_count
  },

  getChat: state => (scopePath, partnerId) => {
    const selectedChatScope = state.chats && state.chats.find(chat => chat.path === scopePath)

    if (!selectedChatScope) {
      return null
    }

    return findChat(selectedChatScope, partnerId)
  },

  getChatPartnerId: state => (scopePath, chatPath) => {
    if (scopePath === MEMBER_CHAT_SCOPE) {
      const selectedChat =
        state.chats &&
        state.chats
          .find(chat => chat.path === scopePath)
          .chats.find(chat => {
            return chat.partner.publication.subdomain === chatPath
          })

      return selectedChat && selectedChat.partner.publication.id
    } else {
      const selectedChat =
        state.chats &&
        state.chats
          .find(chat => chat.path === scopePath)
          .chats.find(chat => {
            return chat.partner.member.id === chatPath
          })

      return selectedChat && selectedChat.partner.member.id
    }
  },

  getPublicationId: state => (scopePath, partnerId) => {
    const scope = state.chats && state.chats.find(chat => chat.path === scopePath)

    if (!scope) return null

    if (scope.publication && scope.publication.id) {
      // publication scope
      return scope.publication.id
    } else {
      // 'me' scope
      return partnerId
    }
  },

  getNotifications: state => {
    return state.notifications
  }
}

const onMessageCreated = store => async payload => {
  await store.dispatch('showNotification', {
    id: MESSAGE_SENT_NOTIFICATION + payload.message.id,
    type: MESSAGE_SENT_NOTIFICATION,
    data: {}
  })

  if (payload.recipient_id === payload.publication_id) {
    // The connected user is the one who sent the message and the message
    // is addressed to a publisher
    await store.dispatch('setNewMessage', {
      message: payload.message,
      scope: MEMBER_CHAT_SCOPE,
      partnerId: payload.publication_id,
      context: 'user'
    })

    store.commit('reorderChatsByLastInsertedAt', MEMBER_CHAT_SCOPE)
  } else {
    // The connected user is the one who sent the message and the message
    // is addressed to a member
    const scope = store.state.chats.find(s => {
      return s.path !== MEMBER_CHAT_SCOPE && s.publication.id === payload.publication_id
    })

    await store.dispatch('setNewMessage', {
      message: payload.message,
      scope: scope.path,
      partnerId: payload.recipient_id,
      context: 'publication'
    })

    store.commit('reorderChatsByLastInsertedAt', scope.path)
  }
}

const onNewMessage = store => async payload => {
  if (payload.recipient_id === payload.publication_id) {
    // The connected user is the recipient of the message and a publisher
    const scope = store.state.chats.find(s => {
      return s.path !== MEMBER_CHAT_SCOPE && s.publication.id === payload.publication_id
    })

    await store.dispatch('setNewMessage', {
      message: payload.message,
      scope: scope.path,
      partnerId: payload.sender_id,
      context: 'publication'
    })

    store.commit('reorderChatsByLastInsertedAt', scope.path)

    await store.dispatch('showNotification', {
      id: NEW_MESSAGE_NOTIFICATION + payload.message.id,
      type: NEW_MESSAGE_NOTIFICATION,
      data: { scope: scope.path, partner: store.getters.getChat(scope.path, payload.sender_id).partner }
    })
  } else {
    // The connected user is the recipient of the message and a member
    await store.dispatch('setNewMessage', {
      message: payload.message,
      scope: MEMBER_CHAT_SCOPE,
      partnerId: payload.publication_id,
      context: 'user'
    })

    store.commit('reorderChatsByLastInsertedAt', MEMBER_CHAT_SCOPE)

    await store.dispatch('showNotification', {
      id: NEW_MESSAGE_NOTIFICATION + payload.message.id,
      type: NEW_MESSAGE_NOTIFICATION,
      data: {
        scope: MEMBER_CHAT_SCOPE,
        partner: store.getters.getChat(MEMBER_CHAT_SCOPE, payload.publication_id).partner
      }
    })
  }
}

export default function ({ defaultChatScope }) {
  return createStore({
    state: { ...initialState, defaultChatScope },
    mutations,
    actions,
    getters
  })
}

export {
  initialState,
  mutations,
  actions,
  getters,
  onMessageCreated,
  onNewMessage,
  NEW_MESSAGE_NOTIFICATION,
  MESSAGE_SENT_NOTIFICATION,
  REMOVE_NOTIFICATION_AFTER
}
